import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/buttons/Button';
import BackButton from '../components/buttons/BackButton';
import Card from '../components/Card/Card';
import SectionHeader from '../components/SectionHeader/SectionHeader';
import { isCapacitor } from '../utils/platform';
import './Subscription.css';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import SubscriptionBadgeCircle from '../Dictaphone/design/SubscriptionBadgeCircle';
import { createLogger } from '../services/Debug';
import { subscriptionService } from '../services';
import { openExternalUrl, onBrowserClosed } from '../utils/browserHelper';
import { isIOS } from '../utils/platform';
import { 
  FaCreditCard, 
  FaExclamationTriangle, 
  FaHeadset, 
  FaQuestionCircle,
  FaChartLine,
  FaCrown,
  FaCalendarAlt,
  FaInfoCircle
} from 'react-icons/fa';

function SubscriptionManagement() {
  const log = createLogger('SubscriptionManagement');
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  
  
  const navigate = useNavigate();
  const { socket } = useSocket();
  const { authReady, profile, refreshProfile } = useAuth();

  useEffect(() => {
    fetchSubscriptionStatus();
    // Also fetch current subscription details from Stripe (authoritative)
    fetchStripeCurrentSubscription();
  }, []);

  // Listen for browser close events (when Stripe Portal/Checkout closes)
  useEffect(() => {
    const cleanup = onBrowserClosed(async () => {
      log.info('Browser closed, refreshing profile and subscription data');
      try {
        await refreshProfile();
        await fetchSubscriptionStatus();
        await fetchStripeCurrentSubscription();
      } catch (error) {
        log.error('Error refreshing after browser close:', error);
      }
    });

    return cleanup;
  }, []);

  // Listen for real-time subscription updates
  useEffect(() => {
    if (!socket) return;

    const handleSubscriptionUpdate = (data) => {
      
      if (data.type === 'subscription_updated') {
        setSubscription(data.subscription);
        refreshProfile();
      } else if (data.type === 'trial_ending') {
        // Show notification about trial ending
        setSubscription(prev => ({
          ...prev,
          trialEnding: true,
          trialEndDate: data.endDate
        }));
      } else if (data.type === 'payment_failed') {
        setError('Le dernier paiement a échoué. Veuillez mettre à jour vos informations de paiement.');
      }
    };

    socket.on('subscription_update', handleSubscriptionUpdate);

    return () => {
      socket.off('subscription_update', handleSubscriptionUpdate);
    };
  }, [socket, refreshProfile]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await subscriptionService.getSubscriptionStatus();
      
      if (response.subscription) {
        setSubscription(response.subscription);
      }
    } catch (error) {
      log.error('Error fetching subscription status:', error);
      setError('Impossible de charger les informations d\'abonnement');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStripeCurrentSubscription = async () => {
    try {
      const resp = await subscriptionService.getCurrentSubscription();
      const apiSub = resp?.data?.subscription || resp?.subscription;
      if (apiSub) {
        // Merge/normalize into our local shape where possible
        setSubscription(prev => ({
          ...(prev || {}),
          id: apiSub.id || prev?.id,
          status: apiSub.status || prev?.status,
          planId: apiSub.planType || prev?.planId,
          planType: apiSub.planType || prev?.planType,
          planName: apiSub.planName || prev?.planName,
          priceId: apiSub.priceId || prev?.priceId,
          amount: apiSub.amount != null ? apiSub.amount : prev?.amount,
          currency: apiSub.currency || prev?.currency,
          interval: apiSub.interval || prev?.interval,
          currentPeriodStart: apiSub.currentPeriodStart || prev?.currentPeriodStart,
          currentPeriodEnd: apiSub.currentPeriodEnd || prev?.currentPeriodEnd,
          cancelAtPeriodEnd: apiSub.cancelAtPeriodEnd != null ? apiSub.cancelAtPeriodEnd : prev?.cancelAtPeriodEnd,
          trialEnd: apiSub.trialEnd != null ? apiSub.trialEnd : prev?.trialEnd
        }));
        // Refresh profile to reflect any changes quickly
        try { await refreshProfile(); } catch (_) {}
      }
    } catch (e) {
      log.warn('Failed to fetch current Stripe subscription:', e);
      // Non-fatal; UI will continue with existing data
    }
  };

  const handleManageSubscription = async () => {
    setIsRedirecting(true);
    try {
      const response = await subscriptionService.getPortalUrl();

      if (response.url) {
        // Open Stripe Portal (in-app browser on mobile, redirect on web)
        await openExternalUrl(response.url);
      }
    } catch (error) {
      log.error('Error creating portal session:', error);
      alert('Impossible d\'accéder au portail de gestion');
    } finally {
      setIsRedirecting(false);
    }
  };

  const handleUpgrade = () => {
    navigate('/subscription/plans');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // Ajout d'une fonction pour obtenir le planType à partir du profile, du context ou du subscription
  const getPlanType = () => {
    if (profile && (profile.planType || profile?.subscription?.planType)) {
      return profile.planType || profile.subscription.planType;
    }
    return subscription?.planId || null;
  };

  const getPlanDisplayName = () => {
    const planType = getPlanType();
    if (!planType) return 'N/A';
    if (isIOS && planType.toLowerCase() === 'silver') return 'Mobile';
    return planType.charAt(0).toUpperCase() + planType.slice(1);
  };

  const getStatusBadgeInfo = () => {
    if (!subscription) return null;
    
    const status = subscription.status;
    switch (status) {
      case 'active':
        return { text: 'Actif', className: 'status-badge active' };
      case 'trialing':
        return { text: 'Période d\'essai', className: 'status-badge trialing' };
      case 'past_due':
        return { text: 'Paiement en retard', className: 'status-badge past_due' };
      case 'canceled':
        return { text: 'Annulé', className: 'status-badge canceled' };
      default:
        return { text: status, className: 'status-badge' };
    }
  };

  if (isLoading || !authReady || !profile) {
    return (
      <div className="subscription-container">
        <div className="loading-message">Chargement...</div>
      </div>
    );
  }

  if (error && !subscription) {
    return (
      <>
        {isCapacitor && (
          <div className="capacitor-topbar">
            <div className="topbar-content">
              <img src={`${process.env.PUBLIC_URL}/icon.png`} alt="Logo" className="topbar-logo" />
              <h2 className="topbar-title">voxia</h2>
            </div>
          </div>
        )}
        
        <div className="subscription-container">
          <BackButton to="/settings" />
          <Card className="card-large">
            <div className="subscription-message">
              <div className="cancel-icon">
                <FaExclamationTriangle style={{ fontSize: '50px', color: 'var(--color-danger)' }} />
              </div>
              <h1 className="message-title">Erreur</h1>
              <p className="message-text">{error}</p>
              <Button
                variant="secondary"
                onClick={() => navigate('/settings')}
                icon={FaInfoCircle}
              >
                Retour aux paramètres
              </Button>
            </div>
          </Card>
        </div>
      </>
    );
  }

  const isTrialActive = subscription?.status === 'trialing';
  const trialDaysRemaining = isTrialActive ? getDaysRemaining(subscription.trialEnd) : 0;
  const statusBadgeInfo = getStatusBadgeInfo();

  return (
    <>
      {isCapacitor && (
        <div className="capacitor-topbar">
          <div className="topbar-content">
            <img src={`${process.env.PUBLIC_URL}/icon.png`} alt="Logo" className="topbar-logo" />
            <h2 className="topbar-title">voxia</h2>
          </div>
        </div>
      )}

      <div className="subscription-container">
        <BackButton to="/settings" />
        
        {/* Error Alert */}
        {error && (
          <Card className="card-danger" style={{ borderLeft: '4px solid var(--color-danger)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FaExclamationTriangle style={{ color: 'var(--color-danger)', fontSize: '24px', flexShrink: 0 }} />
              <p style={{ margin: 0, color: 'var(--color-danger)' }}>{error}</p>
            </div>
          </Card>
        )}

        {/* Subscription Management Card */}
        <Card className="card-large">
          <SectionHeader 
            icon={FaCrown} 
            title="Gestion de votre abonnement" 
            
          />
          
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: '8px',
            marginTop: '12px'
          }}>
            {/* Plan Badge and Name */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '32px',
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              <SubscriptionBadgeCircle planType={getPlanType()} size={100} />
              <div style={{ textAlign: 'center' }}>
                <h2 style={{ 
                  fontSize: '18px', 
                  fontWeight: '600', 
                  color: 'var(--color-dark)',
                  margin: '0 0 8px 0'
                }}>
                  Plan {getPlanDisplayName()}
                </h2>
                {statusBadgeInfo && (
                  <span className={statusBadgeInfo.className}>
                    {statusBadgeInfo.text}
                  </span>
                )}
              </div>
            </div>

            {/* Trial Info */}
            {isTrialActive && (
              <div className="trial-info" style={{ width: '100%', maxWidth: '500px' }}>
                <div className="trial-icon">
                  <FaCalendarAlt style={{ fontSize: '14px', color: '#ef6c00' }} />
                </div>
                <div className="trial-content" style={{ fontSize: '12px' }}>
                  <div className="trial-title">Période d'essai</div>
                  <div className="trial-days" style={{ marginTop: 2 }}>
                    {trialDaysRemaining} jours restants · fin {formatDate(subscription.trialEnd)}
                  </div>
                </div>
              </div>
            )}

            {/* Billing Info */}
            {subscription.currentPeriodEnd && (
              <div style={{ 
                textAlign: 'center',
                fontSize: '13px',
                color: '#6b7280',
                marginTop: '4px'
              }}>
                {subscription.cancelAtPeriodEnd ? (
                  <span style={{ color: 'var(--color-danger)', fontWeight: '500' }}>
                    Annulation prévue le {formatDate(subscription.currentPeriodEnd)}
                  </span>
                ) : (
                  <span>
                    Prochaine facturation: {formatDate(subscription.currentPeriodEnd)}
                  </span>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="subscription-actions" style={{ marginTop: '8px' }}>
              <Button
                variant="primary"
                onClick={handleManageSubscription}
                disabled={isRedirecting}
                icon={FaCreditCard}
                
              >
                {isRedirecting ? 'Redirection...' : 'Gérez votre abonnement'}
              </Button>
              
            </div>
          </div>
        </Card>


        {/* Help Section */}
        <Card>
          <SectionHeader 
            icon={FaQuestionCircle} 
            title="Besoin d'aide ?" 
            variant="secondary"
          />
          <div className="help-cards" style={{ marginTop: '20px' }}>
            <div className="help-card">
              <div className="help-icon">
                <FaHeadset style={{ fontSize: '24px', color: '#666' }} />
              </div>
              <h4>Support</h4>
              <p>Notre équipe est là pour vous aider</p>
              <button
                onClick={() => navigate('/contact')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-primary)',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                Contacter le support
              </button>
            </div>
            
            <div className="help-card">
              <div className="help-icon">
                <FaQuestionCircle style={{ fontSize: '24px', color: '#666' }} />
              </div>
              <h4>FAQ</h4>
              <p>Réponses aux questions fréquentes</p>
              <a href="/guide#faq" style={{ color: 'var(--color-primary)', fontWeight: '600' }}>
                Consulter la FAQ
              </a>
            </div>
          </div>
        </Card>

        {/* Footer Info */}
        <Card>
          <div style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>
            <p style={{ marginBottom: '8px' }}>
              <strong>Paiement sécurisé</strong> via Stripe
            </p>
            <p>
              Les modifications prennent effet immédiatement.
              Vous pouvez annuler votre abonnement à tout moment.
            </p>
          </div>

          <div style={{
            textAlign: 'center',
            color: '#999',
            fontSize: '12px',
            marginTop: '20px',
            padding: '0 16px',
            lineHeight: '1.6'
          }}>
            <div>
              En souscrivant, vous acceptez nos{' '}
              <span
                onClick={() => navigate('/terms')}
                style={{
                  color: '#999',
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
              >
                conditions générales de vente
              </span>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
}

export default SubscriptionManagement;